import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Card, CardContent, CardActions, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Alert,
  CircularProgress, Avatar, Divider, Tooltip, IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Description, Add, Edit, Archive, ContentCopy,
  Publish, Search, Visibility, WorkspacePremium,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const LEVELS: Record<string, { label: string; color: string }> = {
  intern:    { label: 'Intern',     color: '#78909c' },
  junior:    { label: 'Junior',     color: '#1565c0' },
  mid:       { label: 'Mid',        color: '#2e7d32' },
  senior:    { label: 'Senior',     color: '#6a1b9a' },
  lead:      { label: 'Lead',       color: '#e65100' },
  manager:   { label: 'Manager',    color: '#880e4f' },
  director:  { label: 'Director',   color: '#4527a0' },
  vp:        { label: 'VP',         color: '#b71c1c' },
  c_suite:   { label: 'C-Suite',    color: '#1b5e20' },
};

const STATUS_COLOR: Record<string, string> = {
  draft: '#f57f17', active: '#2e7d32', archived: '#78909c',
};

interface JdTemplate {
  id: string; title: string; department?: string; function?: string;
  level?: string; location?: string; employmentType?: string;
  summary?: string; responsibilities?: string; requirements?: string;
  niceToHave?: string; benefits?: string; tags?: string[];
  useCount: number; status: string;
  createdBy?: { firstName: string; lastName: string };
  updatedAt: string;
}

interface Stats {
  active: number; draft: number; archived: number;
  topUsed: JdTemplate[]; byDept: { department: string; count: string }[];
}

const emptyForm = {
  title: '', department: '', function: '', level: 'mid',
  location: '', employmentType: 'Full-time',
  summary: '', responsibilities: '', requirements: '',
  niceToHave: '', benefits: '', tags: '',
};

const SECTION_PLACEHOLDER: Record<string, string> = {
  responsibilities: '- Design and build features\n- Collaborate with the team\n- Write tests and documentation',
  requirements: '- X+ years of experience\n- Proficiency in ...\n- Strong communication skills',
  niceToHave: '- Experience with ...\n- Background in ...',
  benefits: '- Competitive salary\n- Medical insurance\n- Flexible working',
};

function TemplateSection({ title, content }: { title: string; content?: string }) {
  if (!content) return null;
  const lines = content.split('\n');
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>{title}</Typography>
      {lines.map((l, i) => (
        <Typography key={i} variant="body2" color="text.secondary" sx={{ ml: l.startsWith('-') ? 1 : 0 }}>
          {l}
        </Typography>
      ))}
    </Box>
  );
}

export default function JdTemplates() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');
  const canEdit = isHR || (user as any)?.role === 'manager';

  const [tab, setTab] = useState(0);
  const [templates, setTemplates] = useState<JdTemplate[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [viewItem, setViewItem] = useState<JdTemplate | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const STATUS_TABS = isHR ? ['Active', 'Drafts', 'Archived'] : ['All Active'];

  const statusForTab: Record<number, string | undefined> = isHR
    ? { 0: 'active', 1: 'draft', 2: 'archived' }
    : { 0: 'active' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusForTab[tab]) params.set('status', statusForTab[tab]!);
      if (deptFilter) params.set('department', deptFilter);
      if (search) params.set('search', search);

      const calls: Promise<any>[] = [apiFetch(`/api/jd-templates?${params}`)];
      if (isHR) calls.push(apiFetch('/api/jd-templates/stats'));

      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setTemplates(results[0].value ?? []);
      if (results[1] && results[1].status === 'fulfilled') setStats((results[1] as any).value);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [tab, deptFilter, search, isHR]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditId(null); setFormData({ ...emptyForm }); setFormOpen(true);
  };

  const openEdit = (t: JdTemplate) => {
    setEditId(t.id);
    setFormData({
      title: t.title, department: t.department || '', function: t.function || '',
      level: t.level || 'mid', location: t.location || '',
      employmentType: t.employmentType || 'Full-time',
      summary: t.summary || '', responsibilities: t.responsibilities || '',
      requirements: t.requirements || '', niceToHave: t.niceToHave || '',
      benefits: t.benefits || '', tags: (t.tags || []).join(', '),
    });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      if (editId) {
        await apiFetch(`/api/jd-templates/${editId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await apiFetch('/api/jd-templates', { method: 'POST', body: JSON.stringify(payload) });
      }
      setFormOpen(false);
      load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const publish = async (id: string) => {
    await apiFetch(`/api/jd-templates/${id}/publish`, { method: 'PATCH' });
    load();
  };

  const archive = async (id: string) => {
    await apiFetch(`/api/jd-templates/${id}/archive`, { method: 'PATCH' });
    load();
  };

  const duplicate = async (id: string) => {
    await apiFetch(`/api/jd-templates/${id}/duplicate`, { method: 'POST' });
    load();
  };

  const departments = templates.map(t => t.department).filter((d): d is string => !!d).filter((d, i, a) => a.indexOf(d) === i);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#4527a0', width: 48, height: 48 }}><Description /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>JD Templates</Typography>
            <Typography variant="body2" color="text.secondary">
              Reusable job description library for recruitment
            </Typography>
          </Box>
        </Box>
        {canEdit && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}
            sx={{ bgcolor: '#4527a0', '&:hover': { bgcolor: '#311b92' } }}>
            New Template
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats (HR) */}
      {isHR && stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Active', value: stats.active, color: '#2e7d32' },
            { label: 'Drafts', value: stats.draft, color: '#f57f17' },
            { label: 'Archived', value: stats.archived, color: '#78909c' },
            { label: 'Most Used', value: stats.topUsed[0]?.title?.slice(0, 16) ?? '—', color: '#4527a0' },
          ].map(s => (
            <Grid size={{ xs: 6, sm: 3 }} key={s.label}>
              <Paper sx={{ p: 2, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                <Typography variant="h5" fontWeight={700} color={s.color}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Search + filter */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Search templates…" value={search}
          onChange={e => setSearch(e.target.value)} sx={{ minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Department" value={deptFilter}
          onChange={e => setDeptFilter(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All Departments</MenuItem>
          {departments.map(d => <MenuItem key={d} value={d!}>{d}</MenuItem>)}
        </TextField>
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {STATUS_TABS.map((t, i) => <Tab key={i} label={t} />)}
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : templates.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Description sx={{ fontSize: 64, opacity: 0.3, mb: 1 }} />
          <Typography>No templates found</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {templates.map(t => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.id}>
              <Card sx={{
                height: '100%', display: 'flex', flexDirection: 'column',
                borderLeft: `4px solid ${t.level ? LEVELS[t.level]?.color : '#4527a0'}`,
                '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }, transition: 'all 0.2s',
              }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>{t.title}</Typography>
                    {isHR && (
                      <Chip size="small" label={t.status}
                        sx={{ bgcolor: STATUS_COLOR[t.status] + '20', color: STATUS_COLOR[t.status], fontSize: '0.65rem' }} />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    {t.department && <Chip size="small" label={t.department} variant="outlined" sx={{ fontSize: '0.65rem' }} />}
                    {t.level && (
                      <Chip size="small" label={LEVELS[t.level]?.label ?? t.level}
                        sx={{ bgcolor: (LEVELS[t.level]?.color ?? '#666') + '15', color: LEVELS[t.level]?.color ?? '#666', fontSize: '0.65rem' }} />
                    )}
                    {t.employmentType && <Chip size="small" label={t.employmentType} variant="outlined" sx={{ fontSize: '0.65rem' }} />}
                  </Box>
                  {t.summary && (
                    <Typography variant="body2" color="text.secondary"
                      sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {t.summary}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Visibility fontSize="small" sx={{ color: 'text.secondary', fontSize: '0.85rem' }} />
                    <Typography variant="caption" color="text.secondary">Used {t.useCount}x</Typography>
                    {t.tags?.slice(0, 2).map(tag => (
                      <Chip key={tag} size="small" label={tag} sx={{ fontSize: '0.6rem', height: 16 }} />
                    ))}
                  </Box>
                </CardContent>
                <CardActions sx={{ borderTop: '1px solid #f0f0f0', pt: 0 }}>
                  <Button size="small" startIcon={<Visibility />} onClick={() => setViewItem(t)}>View</Button>
                  {canEdit && (
                    <>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(t)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Duplicate"><IconButton size="small" onClick={() => duplicate(t.id)}><ContentCopy fontSize="small" /></IconButton></Tooltip>
                      {isHR && t.status === 'draft' && (
                        <Tooltip title="Publish"><IconButton size="small" color="success" onClick={() => publish(t.id)}><Publish fontSize="small" /></IconButton></Tooltip>
                      )}
                      {isHR && t.status === 'active' && (
                        <Tooltip title="Archive"><IconButton size="small" onClick={() => archive(t.id)}><Archive fontSize="small" /></IconButton></Tooltip>
                      )}
                    </>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* View Dialog */}
      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="md" fullWidth>
        {viewItem && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{viewItem.title}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    {viewItem.department && <Chip size="small" label={viewItem.department} />}
                    {viewItem.level && <Chip size="small" label={LEVELS[viewItem.level]?.label ?? viewItem.level}
                      sx={{ bgcolor: (LEVELS[viewItem.level]?.color ?? '#666') + '15', color: LEVELS[viewItem.level]?.color ?? '#666' }} />}
                    {viewItem.location && <Chip size="small" label={`📍 ${viewItem.location}`} variant="outlined" />}
                    {viewItem.employmentType && <Chip size="small" label={viewItem.employmentType} variant="outlined" />}
                  </Box>
                </Box>
                {canEdit && <Button startIcon={<Edit />} size="small" onClick={() => { setViewItem(null); openEdit(viewItem); }}>Edit</Button>}
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {viewItem.summary && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1">{viewItem.summary}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <TemplateSection title="Responsibilities" content={viewItem.responsibilities} />
              <TemplateSection title="Requirements" content={viewItem.requirements} />
              <TemplateSection title="Nice to Have" content={viewItem.niceToHave} />
              <TemplateSection title="What We Offer" content={viewItem.benefits} />
              {viewItem.tags && viewItem.tags.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {viewItem.tags.map(tag => <Chip key={tag} size="small" label={tag} sx={{ mr: 0.5 }} />)}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Used {viewItem.useCount} time{viewItem.useCount !== 1 ? 's' : ''} · Last updated {new Date(viewItem.updatedAt).toLocaleDateString()}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewItem(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? 'Edit Template' : 'New JD Template'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Job Title" value={formData.title}
              onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} required />
            <TextField select label="Level" value={formData.level}
              onChange={e => setFormData(p => ({ ...p, level: e.target.value }))} sx={{ minWidth: 120 }}>
              {Object.entries(LEVELS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Department" value={formData.department}
              onChange={e => setFormData(p => ({ ...p, department: e.target.value }))} />
            <TextField fullWidth label="Function" value={formData.function}
              onChange={e => setFormData(p => ({ ...p, function: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Location" value={formData.location}
              onChange={e => setFormData(p => ({ ...p, location: e.target.value }))} />
            <TextField select fullWidth label="Employment Type" value={formData.employmentType}
              onChange={e => setFormData(p => ({ ...p, employmentType: e.target.value }))}>
              {['Full-time', 'Part-time', 'Contract', 'Internship'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
          </Box>
          <TextField fullWidth label="Role Summary" multiline rows={2} value={formData.summary}
            onChange={e => setFormData(p => ({ ...p, summary: e.target.value }))}
            placeholder="Brief overview of the role and its impact" />
          <TextField fullWidth label="Responsibilities" multiline rows={4} value={formData.responsibilities}
            onChange={e => setFormData(p => ({ ...p, responsibilities: e.target.value }))}
            placeholder={SECTION_PLACEHOLDER.responsibilities} />
          <TextField fullWidth label="Requirements (must-have)" multiline rows={4} value={formData.requirements}
            onChange={e => setFormData(p => ({ ...p, requirements: e.target.value }))}
            placeholder={SECTION_PLACEHOLDER.requirements} />
          <TextField fullWidth label="Nice to Have" multiline rows={3} value={formData.niceToHave}
            onChange={e => setFormData(p => ({ ...p, niceToHave: e.target.value }))}
            placeholder={SECTION_PLACEHOLDER.niceToHave} />
          <TextField fullWidth label="What We Offer / Benefits" multiline rows={3} value={formData.benefits}
            onChange={e => setFormData(p => ({ ...p, benefits: e.target.value }))}
            placeholder={SECTION_PLACEHOLDER.benefits} />
          <TextField fullWidth label="Tags (comma-separated)" value={formData.tags}
            onChange={e => setFormData(p => ({ ...p, tags: e.target.value }))}
            placeholder="engineering, backend, typescript" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !formData.title}
            sx={{ bgcolor: '#4527a0' }}>
            {saving ? 'Saving…' : editId ? 'Update' : 'Save Draft'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
